import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { breakpoints, css } from 'src/styles/theme';
import { rem } from 'polished';
import Main from '@components/Layout/Main';
import FAQs from '@components/FAQs';
import { useDispatch } from 'react-redux';
import UIDuck from '../redux/commonDucks/ui.duck';
import useTranslations from '@hooks/useTranslations';
import Characteristics from '@components/v2/Characteristics';
import { BRAND, isBrandAC, isBrandDS, isBrandOV, isMarketGB } from '../constants/main';
import { ConfigLeftSection, ConfigMainGrid, ConfigRightSection, MobileSection, StyledGrid } from '@components/Grid';
import { getSessionIdCookie } from '@utils/Session.utils';
import { NextPage } from 'next';
import ImageSection from '@components/v2/ImageSection';
import RedirectionModal from '@components/RedirectionModal';
import isEmpty from 'lodash/isEmpty';
import { useCarDetailsDuck } from '@hooks/useCarDetailsDuck';
import SummaryTitle from '@components/SummaryTitle';
import VehicleSummary from '@components/v2/VehicleSummary';
import SummaryPriceDetails from '@components/SummaryPriceDetails';
import SummaryDealerDetails from '@components/SummaryDealerDetails';
import SummaryLegalText from '@components/SummaryLegalText';
import { useCarStockJourneyCheck } from '@hooks/useCarStockJourneyCheck';
import { getInitialPropsBasicDeal } from '@utils/getInitialProps/getInitialPropsBasicDeal';
import FinanceWidget from '@components/FinanceWidget';
import { useFeatureSwitchEnabled } from '@hooks/useFeatureSwitchEnabled';
import { useGTM } from '@hooks/useGTM';
import { FEATURES_LIST } from 'src/context/featureSwitchApp';
import { GtmData } from 'src/types/gtm';
import { GTM_EVENT_AB_TEST, GTM_MAIN_STEP } from 'src/constants/gtm';
import { useJourneyType } from '@hooks/useJourneyType';
import { DealService, getOptionsList } from 'src/services';
import NotReadyToBuyOnline from '@components/NotReadyToBuyOnline';
import VehicleDetailsHeadingInformation from '@components/v2/VehicleDetailsHeadingInformation';
import SaveYourProjectAndContinue from '@components/v2/SaveYourProjectAndContinue';
import CantFindYourDreamCar from '@components/v2/CantFindYourDreamCar';
import { Pages } from 'src/types/pages';
import GlobalDuck from 'src/redux/global/global.duck';
import { useEffectOnce } from 'react-use';
import { useExperiment } from '@hooks/useExperiment';
import { useUserDuck } from '@hooks/useUserDuck';
import useLogoutListener from '@hooks/useLogoutListener';
import useSessionChangeListener from '@hooks/useSessionChangeListener';
import { useGlobalDuck } from '@hooks/useGlobalDuck';
import SummaryOptions from '@components/SummaryOptions';
import ConfiguratorDuck from '../redux/configurator/configurator.duck';
import { ICarOptionDetail } from '@components/Modal/ModalCarOptionDetail/ModalCarOptionDetailTemplate';
import { useFeatureSwitch } from '@hooks/useFeatureSwitch';
import { IConfiguratorOption } from '../redux/configurator/configurator.duck.interface';
import { usePlausible, UserEvent } from '@hooks/usePlausible';
import { PosthogTrigger } from '../constants/enums';

type ISummary = {};

const SummaryContainer = styled.div`
    #carGallery {
        padding-bottom: unset;
        padding-left: ${rem('10px')};
        padding-right: ${rem('10px')};

        @media (min-width: ${breakpoints.sm}px) {
            padding-left: unset;
            padding-right: unset;
        }
    }
`;

export const Divider = styled.div<{ isBottom?: boolean; isOpen?: boolean }>`
    position: absolute;
    left: 0;
    height: 1px;
    width: 100%;
    display: flex;
    background: ${({ theme }) => theme.colors.grey12};

    @media (min-width: ${breakpoints.sm}px) {
        display: none;
    }

    ${({ isBottom, isOpen }) =>
        isBottom &&
        isOpen &&
        css`
            margin-top: ${rem('20px')};
        `}

    ${isMarketGB &&
    isBrandOV &&
    css`
        background: #73767a;
    `}

    ${!isMarketGB &&
    isBrandOV &&
    css`
        background: black;
    `}

    ${isBrandDS &&
    css`
        background: ${({ theme }) => theme.colors.secondary};
    `}
    
    ${isBrandAC &&
    css`
        background: ${({ theme }) => theme.colors.grey2};
    `}
`;

const Summary: NextPage<ISummary> = () => {
    const { isUserLoggedInAsDealer } = useUserDuck();
    const { t } = useTranslations();
    const dispatch = useDispatch();
    const { carDetails, currentDeal } = useCarDetailsDuck();
    const [isOpen, setIsOpen] = useState<boolean>(false);
    const { paymentJourneyType } = useJourneyType();
    const { isStockJourney } = useCarStockJourneyCheck();
    const [isOpenPriceDetails, setIsOpenPriceDetails] = useState<boolean>(true);
    const { isTracked } = useGlobalDuck();
    const { pushTrigger, getPageNameByUrl } = useExperiment();
    const { plausibleUserEvent } = usePlausible();
    const { isUpsellOptionsSectionEnabledOnSummaryPage } = useFeatureSwitch();

    const isPosthogEnabled = useFeatureSwitchEnabled(FEATURES_LIST.FEATURE_SWITCH_POSTHOG_ENABLED);

    const [renderWidget, setRenderWidget] = useState<() => void>();

    const { pushSummaryPage, pushProductDetailImpression, pushToDataLayer } = useGTM();

    const { handleTabChange } = useSessionChangeListener();

    useEffect(() => {
        document?.addEventListener('visibilitychange', handleTabChange);
    }, []);

    useEffect(() => {
        if (
            isUpsellOptionsSectionEnabledOnSummaryPage &&
            !isEmpty(currentDeal?.fullProductConfiguration?.upsellOptions ?? [])
        ) {
            dispatch(UIDuck.setLoading(5));

            const selectedOptionIds = carDetails?.options?.map((option: IConfiguratorOption) => option.id) || [];

            getOptionsList(
                carDetails?.externalId,
                selectedOptionIds?.length > 0 ? selectedOptionIds : null,
                paymentJourneyType
            ).then((optionsListResponse) => {
                const responseOptions = optionsListResponse.data.options;
                dispatch(ConfiguratorDuck.setOptions(responseOptions));
                dispatch(
                    ConfiguratorDuck.setAvailableOptions(
                        responseOptions
                            ?.filter((option: ICarOptionDetail) => option.upsell)
                            ?.filter(
                                (carOption: ICarOptionDetail) =>
                                    !carDetails?.options
                                        // array of deeplink selected options
                                        ?.filter(
                                            (selectedOption: IConfiguratorOption) =>
                                                !selectedOption?.upsellSelectedByCustomer
                                        )
                                        // checking if available option was already selected by deeplink
                                        ?.find(
                                            (selectedOption: IConfiguratorOption) => selectedOption.id === carOption.id
                                        )
                            )
                            ?.map(({ id }: { id: string }) => id)
                    )
                );
                dispatch(
                    ConfiguratorDuck.setSelectedOptions(
                        currentDeal?.fullProductConfiguration?.options?.map((option: IConfiguratorOption) => option.id)
                    )
                );

                dispatch(UIDuck.setLoading(-5));
            });
        } else {
            dispatch(ConfiguratorDuck.setAvailableOptions([]));
        }
    }, []);

    useEffect(() => {
        window.scrollTo(0, 0);
        if (currentDeal?.status !== 'vehicle_summary') {
            DealService.updateDealStatus('vehicle_summary', getSessionIdCookie());
        }
        pushSummaryPage(carDetails, isStockJourney, paymentJourneyType);
        if (!isStockJourney) {
            pushProductDetailImpression(null, carDetails, false);
        }
        dispatch(UIDuck.openCharacteristics({ data: {}, modalType: '' }));
    }, []);

    useEffect(() => {
        if (isPosthogEnabled && !isTracked) {
            pushToDataLayer({
                event: 'uaevent',
                eventCategory: GTM_EVENT_AB_TEST,
                eventAction: 'summary-page-test', //Campaign name
                eventLabel: 'show', //Variation name
            } as GtmData);
            dispatch(GlobalDuck.setIsTracked(true));
        }
    }, [isTracked]);

    const handleFinanceWidgetBtnClick = () => {
        renderWidget && renderWidget();
    };

    const registerRenderCb = (cb: () => void) => {
        setRenderWidget(() => cb);
    };

    const showRedirectionModal = isEmpty(carDetails) || !currentDeal;

    const isFinanceWidgetDisabled = useFeatureSwitchEnabled(FEATURES_LIST.FEATURE_SWITCH_DISABLED_FINANCE_WIDGET);

    const hasDealer = currentDeal?.fullProductConfiguration?.dealers?.length > 0;

    useLogoutListener();

    return (
        <SummaryContainer>
            <Main title={t('summary.page.title')} whiteBackground>
                {showRedirectionModal ? (
                    <RedirectionModal />
                ) : (
                    <>
                        <StyledGrid brand={BRAND} noPadding>
                            <ConfigMainGrid>
                                <MobileSection className={'mobile-section'}>
                                    <SummaryTitle />
                                    <ImageSection isSummary />
                                    <VehicleDetailsHeadingInformation onClick={() => setIsOpen(true)} />
                                    <ConfigRightSection>
                                        <VehicleSummary isUserLoggedInAsDealer={isUserLoggedInAsDealer} />
                                    </ConfigRightSection>
                                    {isUpsellOptionsSectionEnabledOnSummaryPage && <SummaryOptions />}
                                    <SummaryPriceDetails
                                        isMobile
                                        isOpenPriceDetails={isOpenPriceDetails}
                                        setIsOpenPriceDetails={setIsOpenPriceDetails}
                                        handleFinanceWidgetBtnClick={handleFinanceWidgetBtnClick}
                                    />
                                    {hasDealer && <SummaryDealerDetails page={Pages.summary} />}
                                    <SaveYourProjectAndContinue page={Pages.summary} />
                                    <NotReadyToBuyOnline />
                                    {isStockJourney && <CantFindYourDreamCar />}
                                    <SummaryLegalText />
                                </MobileSection>
                                <ConfigLeftSection hideOnMobile className={'desktop-section'}>
                                    <>
                                        <SummaryTitle />
                                        <ImageSection isSummary />
                                        <VehicleDetailsHeadingInformation onClick={() => setIsOpen(true)} />
                                        {isUpsellOptionsSectionEnabledOnSummaryPage && <SummaryOptions />}
                                        <SummaryPriceDetails
                                            isMobile={false}
                                            isOpenPriceDetails={isOpenPriceDetails}
                                            setIsOpenPriceDetails={setIsOpenPriceDetails}
                                            handleFinanceWidgetBtnClick={handleFinanceWidgetBtnClick}
                                        />
                                        {hasDealer && <SummaryDealerDetails page={Pages.summary} />}
                                        <SaveYourProjectAndContinue page={Pages.summary} />
                                        <NotReadyToBuyOnline />
                                        {isStockJourney && <CantFindYourDreamCar />}
                                        <SummaryLegalText />
                                    </>
                                </ConfigLeftSection>
                                <ConfigRightSection hideOnMobile isSummary withBorder={isBrandDS}>
                                    <VehicleSummary isUserLoggedInAsDealer={isUserLoggedInAsDealer} />
                                </ConfigRightSection>
                            </ConfigMainGrid>
                            {isFinanceWidgetDisabled && <FinanceWidget registerRenderCb={registerRenderCb} />}
                        </StyledGrid>

                        {isOpen && (
                            <div id="characteristics">
                                <Characteristics />
                            </div>
                        )}
                    </>
                )}
                <FAQs />
            </Main>
        </SummaryContainer>
    );
};

Summary.getInitialProps = getInitialPropsBasicDeal;

export default Summary;
